import {
	RegionSelection,
	WORLDWIDE
} from 'components/country-select/region-selection'
import { SIGNALS, SignalId } from './signals'
import { ProtectionLevel, SensitivityMode } from './types'

/** One signal row as the card renders it. */
export interface ProtectionCheck {
	id: SignalId
	enabled: boolean
}

/**
 * Every value the card holds, as one object.
 *
 * `dirty` / `saving` / `saved` are deliberately absent — they say what the user is
 * doing right now, not what the entity is set to, and they never travel or persist.
 */
export interface ProtectionSettings {
	aiEnabled: boolean
	/** Not persisted. Drives the post-save click-detection cascade only (§1.1). */
	keepTracking: boolean
	region: RegionSelection
	protectionLevel: ProtectionLevel
	sensitivityMode: SensitivityMode
	aggressiveness: number
	checks: ProtectionCheck[]
	whitelistOn: boolean
	whitelist: string
}

/**
 * A copy nothing else holds a reference into. `checks` is an array of objects and
 * `region.countries` an object of arrays, so a shallow copy would let two entities
 * share rows.
 */
export const cloneSettings = (
	value: ProtectionSettings
): ProtectionSettings => ({
	...value,
	region: {
		worldwide: value.region.worldwide,
		countries: Object.fromEntries(
			Object.entries(value.region.countries).map(([key, ids]) => [
				key,
				[...ids]
			])
		),
		unplaced: [...value.region.unplaced]
	},
	checks: value.checks.map((check) => ({ ...check }))
})

/** Matches `AssetService.create` — AI mode, block, worldwide, middle sensitivity. */
export const DEFAULT_SETTINGS: ProtectionSettings = {
	aiEnabled: true,
	keepTracking: false,
	region: WORLDWIDE,
	protectionLevel: 'full',
	sensitivityMode: 'automatic',
	aggressiveness: 3,
	checks: SIGNALS.map((signal) => ({ id: signal.id, enabled: false })),
	whitelistOn: false,
	whitelist: ''
}

export const isCheckOn = (
	settings: ProtectionSettings,
	id: SignalId
): boolean => !!settings.checks.find((check) => check.id === id)?.enabled
